import React from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import {
  ProductBreadcrumb,
  ProductHero,
  ProductGallery,
  QuickSpecsTabs,
  KeyFeatures,
  OptionalAccessories,
  Applications,
  WhyChooseLiftile,
  QualitySafetyCommitment,
  DownloadBrochure,
  PremiumFAQ,
  RelatedProducts,
  ProductFinalCTA,
} from "../components/product";
import {
  Activity,
  Anchor,
  ArrowUp,
  Award,
  BadgeCheck,
  Boxes,
  Building2,
  ClipboardCheck,
  Cog,
  Container,
  Database,
  Droplets,
  Factory,
  FileText,
  Flag,
  Frame,
  Fuel,
  Gamepad2,
  Gauge,
  HardHat,
  Home,
  Landmark,
  Layers,
  Move3d,
  Package,
  Printer,
  Route,
  ShieldCheck,
  Sparkles,
  Timer,
  TrendingUp,
  Truck,
  Usb,
  Wrench,
  Zap,
} from "lucide-react";

/* =====================================================================
   REVERSIBLE DRUM CONCRETE MIXER (LRDM Series — LRDM600 / LRDM800 /
   LRDM1050) — dedicated product page. Composed entirely from
   /components/product/* shared primitives. Only product-specific data
   lives in this file.
   ===================================================================== */

const HERO_IMAGE = "/product-reversible-drum-mixer.jpg";

const HERO_SPECS = [
  { Icon: Container, value: "Up to 1050 L", label: "Unmixed Capacity" },
  { Icon: Package, value: "Up to 800 L", label: "Mixed Capacity" },
  { Icon: TrendingUp, value: "Up to 17 m³/hr", label: "Production Capacity" },
  { Icon: Flag, value: "Made in India", label: "Engineered & Manufactured" },
];

const HERO_BADGES = [
  "Hydraulic Drum Drive",
  "High Production Capacity",
  "Owner Inspected",
  "Made in India",
];

const GALLERY = [
  {
    src: "/product-reversible-drum-mixer.jpg",
    caption:
      "LIFTILE LRDM Series Reversible Drum Concrete Mixer — high-volume hydraulic mixing for infrastructure projects",
  },
];

const QUICK_SPECS = [
  { label: "Unmixed Capacity", value: "600 – 1050 L" },
  { label: "Mixed Capacity", value: "400 – 800 L" },
  { label: "Drum Speed", value: "12 – 13 RPM" },
  { label: "Production", value: "8 – 17 m³/hr" },
];

const MODELS = {
  LRDM600: {
    name: "LRDM600 — Contractor Series",
    tagline:
      "Compact 600 L reversible drum mixer for mid-scale commercial and residential sites",
    rows: [
      { label: "Unmixed Capacity", value: "600 Litres" },
      { label: "Mixed Capacity", value: "400 Litres" },
      {
        label: "Prime Mover",
        value: "12 HP Diesel Engine / 12.5 HP Electric Motor",
      },
      { label: "Drum Speed", value: "13 RPM" },
      { label: "Water Tank", value: "120 Litres" },
      { label: "Production Capacity", value: "8 – 10 m³/hr" },
    ],
  },
  LRDM800: {
    name: "LRDM800 — Professional Series",
    tagline:
      "Versatile 800 L reversible drum mixer for high-rise, industrial and precast projects",
    rows: [
      { label: "Unmixed Capacity", value: "800 Litres" },
      { label: "Mixed Capacity", value: "600 Litres" },
      {
        label: "Prime Mover",
        value: "16 HP Diesel Engine / 15 HP Electric Motor",
      },
      { label: "Drum Speed", value: "12 RPM" },
      { label: "Water Tank", value: "120 Litres" },
      { label: "Production Capacity", value: "11 – 13 m³/hr" },
    ],
  },
  LRDM1050: {
    name: "LRDM1050 — Infra Series",
    tagline:
      "Heavy-duty 1050 L flagship for metros, bridges, power plants and infrastructure works",
    rows: [
      { label: "Unmixed Capacity", value: "1050 Litres" },
      { label: "Mixed Capacity", value: "800 Litres" },
      {
        label: "Prime Mover",
        value: "20 HP Diesel Engine / 20 HP Electric Motor",
      },
      { label: "Drum Speed", value: "12 RPM" },
      { label: "Water Tank", value: "270 Litres" },
      { label: "Production Capacity", value: "15 – 17 m³/hr" },
    ],
  },
};

const FEATURES = [
  {
    Icon: Layers,
    title: "Heavy Duty Fabricated Steel Chassis",
    desc: "Precision-welded structural steel chassis engineered for continuous high-volume operation and long service life on demanding infrastructure sites.",
  },
  {
    Icon: Activity,
    title: "Positive Hydraulic Drum Drive",
    desc: "Positive-displacement hydraulic drive delivers smooth, controlled drum rotation for consistent mixing and precise reverse-discharge cycles.",
  },
  {
    Icon: Cog,
    title: "Heavy Duty Hydraulic Transmission",
    desc: "Robust hydraulic transmission built for continuous industrial duty — dependable torque and low-maintenance operation batch after batch.",
  },
  {
    Icon: Gamepad2,
    title: "Joystick Hydraulic Control",
    desc: "Ergonomic joystick control station enables smooth, single-operator drum reverse, discharge and mixing operation with precise fingertip response.",
  },
  {
    Icon: Anchor,
    title: "Four Heavy Duty Stabilizer Jacks",
    desc: "Four hydraulic stabilizer jacks lock the mixer to the ground for vibration-free, stable operation during high-output continuous production.",
  },
  {
    Icon: Gauge,
    title: "Electronic Water Metering System",
    desc: "Precision electronic water metering delivers accurate, repeatable water-to-cement ratios for consistent batch quality every cycle.",
  },
  {
    Icon: Droplets,
    title: "Calibrated Water Tank",
    desc: "Integrated calibrated water tank (120 L / 270 L) with direct site refill access ensures uninterrupted continuous batching.",
  },
  {
    Icon: Container,
    title: "Heavy Duty Mixing Drum",
    desc: "Reversible heavy-gauge steel drum with hardened, replaceable mixing blades delivers thorough, uniform concrete mixing at high volumes.",
  },
  {
    Icon: TrendingUp,
    title: "High Production Efficiency",
    desc: "Delivers up to 17 m³/hr of high-quality concrete — engineered for continuous, high-output infrastructure production.",
  },
  {
    Icon: Sparkles,
    title: "Premium Powder Coated Finish",
    desc: "7-tank pre-treatment with premium powder coating for site-grade corrosion resistance and a lasting professional appearance.",
  },
  {
    Icon: Truck,
    title: "Easy Transportation with Pneumatic Wheels",
    desc: "Heavy-duty pneumatic tyre kit and integrated towbar enable safe, quick site-to-site relocation without a dedicated trailer.",
  },
  {
    Icon: ShieldCheck,
    title: "Reliable Site Performance",
    desc: "Engineered for continuous daily production — dependable batch after batch on the most demanding infrastructure projects.",
  },
  {
    Icon: ClipboardCheck,
    title: "Owner Inspected Before Dispatch",
    desc: "Every LRDM Series mixer is personally inspected and quality signed off before it leaves the LIFTILE factory.",
  },
];

const ACCESSORIES = [
  {
    Icon: Fuel,
    title: "Dual Drive System (Diesel + Electric)",
    desc: "Combined diesel engine and electric motor package — switch power source based on site availability with zero downtime.",
  },
  {
    Icon: Move3d,
    title: "Hopper Vibrator",
    desc: "Integrated hopper vibrator prevents material bridging in the loader hopper — smooth, uninterrupted feed of dry aggregates.",
  },
  {
    Icon: Gauge,
    title: "Digital Batching Monitor",
    desc: "Real-time digital display of batch quantities, cycle times and production totals for full site production visibility.",
  },
  {
    Icon: Usb,
    title: "USB Data Export",
    desc: "Export batch logs, production data and cycle history via USB for site reporting, quality audits and compliance records.",
  },
  {
    Icon: Printer,
    title: "Print Facility",
    desc: "Integrated batch ticket printer produces printed batch records on demand — ideal for QC, dispatch and client documentation.",
  },
  {
    Icon: Droplets,
    title: "Programmable Water Dosing System",
    desc: "Programmable water dosing with pre-set recipes for repeatable water-to-cement ratios across every batch of every mix design.",
  },
  {
    Icon: Database,
    title: "SCADA Integration",
    desc: "Full SCADA-ready integration for centralized batch control, remote monitoring and enterprise-grade production analytics.",
  },
  {
    Icon: BadgeCheck,
    title: "Stainless Steel Drum",
    desc: "Corrosion-resistant stainless steel drum option for demanding marine, chemical or aggressive-environment infrastructure projects.",
  },
  {
    Icon: FileText,
    title: "Load Cell Based Weighing System",
    desc: "High-accuracy load cell weighing system for precise cement, aggregate and additive proportioning on every batch.",
  },
];

const APPLICATIONS = [
  {
    Icon: Building2,
    title: "Commercial Buildings",
    desc: "High-volume batch mixing for offices, malls, hotels, hospitals and mixed-use commercial developments.",
  },
  {
    Icon: Home,
    title: "Residential Projects",
    desc: "Continuous concrete production for large residential complexes, apartment blocks and integrated townships.",
  },
  {
    Icon: ArrowUp,
    title: "High-Rise Construction",
    desc: "Dependable batch mixing for tall residential and commercial towers with continuous daily concrete demand.",
  },
  {
    Icon: Factory,
    title: "Industrial Plants",
    desc: "Continuous concrete production for factories, plants, warehouses and heavy industrial construction sites.",
  },
  {
    Icon: Landmark,
    title: "Infrastructure Projects",
    desc: "High-output batch mixing for metros, treatment plants, power plants, ports and public infrastructure works.",
  },
  {
    Icon: Landmark,
    title: "Bridges",
    desc: "Precision high-volume concrete for bridge girders, decks, piers, abutments and structural elements.",
  },
  {
    Icon: Route,
    title: "Roads & Highways",
    desc: "Dependable batch mixing for concrete roads, kerbs, drains, expressways and highway construction works.",
  },
  {
    Icon: Route,
    title: "Metro Projects",
    desc: "Continuous high-output concrete production for metro rail viaducts, stations, tunnels and depot works.",
  },
  {
    Icon: Container,
    title: "Ready Mix Support",
    desc: "Reliable batching support for ready mix operations, precast yards and central concrete production facilities.",
  },
];

const TRUST_CARDS = [
  {
    Icon: HardHat,
    title: "Heavy Duty Construction",
    desc: "Precision-welded structural steel chassis built for continuous high-volume production on demanding infrastructure sites.",
  },
  {
    Icon: Activity,
    title: "Hydraulic Drum Control",
    desc: "Positive-displacement hydraulic drum drive with joystick control for smooth, precise mixing and reverse-discharge cycles.",
  },
  {
    Icon: Award,
    title: "Premium Components",
    desc: "Trusted branded engines, motors, hydraulics and electronics from leading Indian and global brands.",
  },
  {
    Icon: ClipboardCheck,
    title: "Owner Inspected",
    desc: "Every LRDM Series mixer is personally inspected and quality signed off before dispatch.",
  },
  {
    Icon: Flag,
    title: "Made in India",
    desc: "Designed and manufactured in India for Indian infrastructure site conditions and after-sales support.",
  },
  {
    Icon: TrendingUp,
    title: "High Productivity",
    desc: "Delivers up to 17 m³/hr of high-quality concrete — engineered for continuous, high-output daily production.",
  },
  {
    Icon: ShieldCheck,
    title: "Reliable Performance",
    desc: "Trial-run tested for dependable daily batch mixing — project after project, site after site.",
  },
];

const TECH_HIGHLIGHTS = [
  {
    Icon: Activity,
    title: "Hydraulic Drum Drive",
    desc: "Positive-displacement hydraulic drive with joystick control delivers smooth mixing and precise reverse-discharge cycles.",
  },
  {
    Icon: Gauge,
    title: "Electronic Water Metering",
    desc: "Precision electronic water metering with a calibrated water tank ensures repeatable water-to-cement ratios on every batch.",
  },
  {
    Icon: TrendingUp,
    title: "High Output",
    desc: "Production output from 8 m³/hr on the Contractor Series up to 17 m³/hr on the Infra Series for continuous infrastructure works.",
  },
  {
    Icon: Layers,
    title: "Heavy Duty Chassis",
    desc: "Precision-welded structural steel chassis with four hydraulic stabilizer jacks for vibration-free continuous operation.",
  },
  {
    Icon: Container,
    title: "Precision Mixing",
    desc: "Reversible heavy-gauge drum with hardened mixing blades delivers thorough, uniform concrete at high volumes.",
  },
  {
    Icon: Timer,
    title: "Long Service Life",
    desc: "Premium materials, powder-coated corrosion protection and branded components ensure years of dependable use.",
  },
];

const FAQS = [
  {
    q: "What production capacity can the LRDM Series deliver?",
    a: "The LRDM Series delivers high-volume concrete production ranging from 8 m³/hr up to 17 m³/hr depending on the model.\n\nLRDM600 (Contractor Series) — 8–10 m³/hr for mid-scale commercial and residential sites.\n\nLRDM800 (Professional Series) — 11–13 m³/hr for high-rise, industrial and precast projects.\n\nLRDM1050 (Infra Series) — 15–17 m³/hr for metros, bridges, power plants and heavy infrastructure works.",
  },
  {
    q: "How does the hydraulic drum operation work?",
    a: "The LRDM Series uses a positive-displacement hydraulic drive to rotate the drum. In mixing mode, the drum rotates in one direction to thoroughly blend aggregates, cement and water. To discharge, the operator simply reverses the drum rotation using the joystick control — the internal blade profile then pushes the mixed concrete out through the discharge chute in a single, controlled cycle.\n\nThis reversible design enables fast cycles, clean discharge and significantly higher hourly output compared with traditional tilt-drum mixers.",
  },
  {
    q: "Should I choose diesel or electric operation?",
    a: "Every LRDM Series model is available with either a diesel engine or an electric motor.\n\nChoose diesel if your site does not have reliable three-phase power or you need full mobility across a large infrastructure project.\n\nChoose electric if reliable industrial power supply is available — it delivers lower running costs, cleaner operation and less noise.\n\nYou can also opt for our Dual Drive System accessory to switch between diesel and electric power based on site availability.",
  },
  {
    q: "How does the water metering system work?",
    a: "The LRDM Series is equipped with an electronic water metering system paired with a calibrated on-board water tank (120 L on LRDM600 and LRDM800, 270 L on LRDM1050).\n\nThe operator dials in the required water quantity per batch, and the system automatically delivers a precise, repeatable dose — ensuring consistent water-to-cement ratios and consistent concrete strength across every batch.\n\nThe optional Programmable Water Dosing System adds pre-set mix recipes for even faster site operation.",
  },
  {
    q: "What maintenance does the LRDM Series require?",
    a: "The LRDM Series uses a robust, service-friendly hydraulic design. Routine tasks include periodic drum cleaning, hydraulic oil and filter checks, lubrication of bearings and pivot points, inspection of mixing blades and drum liners for wear, and standard OEM servicing of the diesel engine or electric motor.\n\nEasy access panels are provided at every major service point to keep downtime low.",
  },
  {
    q: "Are spare parts easily available?",
    a: "Yes.\n\nLIFTILE maintains ready inventory of genuine LRDM Series spare parts including mixing blades, drum liners, hydraulic components (pumps, cylinders, valves, hoses), bearings, engine and motor components, and electronics for the water metering system. Contact our spares team to source what you need for your machine.",
  },
  {
    q: "Does LIFTILE provide a warranty on the LRDM Series?",
    a: "Yes.\n\nEvery LIFTILE LRDM Series mixer is backed by our standard manufacturer's warranty on structural components, drum, hydraulic system, prime mover and electronics. Contact our sales team for the current warranty terms applicable to your project and region.",
  },
  {
    q: "Is service and support available across India?",
    a: "Yes.\n\nLIFTILE provides installation, commissioning, spare parts, hydraulic servicing, technical assistance and after-sales support across India through our nationwide dealer and support network. We also offer operator training and site setup guidance on request.",
  },
];

const RELATED_PRODUCTS = [
  {
    slug: "concrete-mixer",
    name: "Concrete Mixer",
    desc: "LCM Series heavy-duty tilting drum mixer for residential, commercial and small-scale contractor concrete production.",
    image: "/product-concrete-mixer.jpg",
  },
  {
    slug: "wheel-barrow",
    name: "Wheel Barrow",
    desc: "Heavy-duty single and double wheel material transport barrows for smooth on-site concrete handling.",
    image: "/product-wheel-barrow.jpg",
  },
  {
    slug: "monkey-lift",
    name: "Monkey Lift",
    desc: "Compact LML250 mini material lifter — the perfect partner for moving mixed concrete to upper floors on tight sites.",
    image: "/product-monkey-lift.jpg",
  },
];

const CTA_BADGES = [
  "Hydraulic Drum Drive",
  "High Production Capacity",
  "Owner Inspected",
  "Made in India",
];

/* ---------- Blueprint SVG for Download Brochure ---------- */
const LRDM_BLUEPRINT_SVG = (
  <svg
    aria-hidden
    viewBox="0 0 400 400"
    className="absolute right-0 top-1/2 -translate-y-1/2 w-[280px] md:w-[360px] h-auto opacity-[0.14] pointer-events-none"
    fill="none"
    stroke="#FF6B1A"
    strokeWidth="1.2"
  >
    {/* Ground line */}
    <line
      x1="20"
      y1="345"
      x2="385"
      y2="345"
      strokeDasharray="4 4"
      opacity="0.5"
    />

    {/* Long trailer / skid chassis */}
    <rect x="55" y="285" width="290" height="20" />
    {/* Cross bracing on chassis */}
    <line
      x1="70"
      y1="285"
      x2="110"
      y2="305"
      strokeDasharray="2 2"
      opacity="0.55"
    />
    <line
      x1="290"
      y1="285"
      x2="330"
      y2="305"
      strokeDasharray="2 2"
      opacity="0.55"
    />

    {/* Towbar */}
    <line x1="20" y1="325" x2="55" y2="295" />
    <circle cx="20" cy="325" r="4" />

    {/* Wheels (twin) */}
    <circle cx="130" cy="335" r="18" />
    <circle cx="130" cy="335" r="7" />
    <circle cx="270" cy="335" r="18" />
    <circle cx="270" cy="335" r="7" />
    {[0, 1, 2, 3, 4, 5].map((i) => {
      const a = (i * Math.PI) / 3;
      return (
        <g key={i} opacity="0.55">
          <line
            x1={130 + Math.cos(a) * 7}
            y1={335 + Math.sin(a) * 7}
            x2={130 + Math.cos(a) * 18}
            y2={335 + Math.sin(a) * 18}
          />
          <line
            x1={270 + Math.cos(a) * 7}
            y1={335 + Math.sin(a) * 7}
            x2={270 + Math.cos(a) * 18}
            y2={335 + Math.sin(a) * 18}
          />
        </g>
      );
    })}

    {/* Four stabilizer jacks (two visible on this profile) */}
    <line x1="72" y1="305" x2="72" y2="355" strokeWidth="2" />
    <line x1="65" y1="355" x2="79" y2="355" strokeWidth="2" />
    <line x1="330" y1="305" x2="330" y2="355" strokeWidth="2" />
    <line x1="323" y1="355" x2="337" y2="355" strokeWidth="2" />

    {/* Reversible drum (horizontal cylinder) */}
    <rect x="105" y="155" width="200" height="120" rx="60" ry="60" />
    {/* Drum trunnion rings */}
    <line x1="140" y1="155" x2="140" y2="275" opacity="0.65" />
    <line x1="270" y1="155" x2="270" y2="275" opacity="0.65" />
    {/* Internal blade indication */}
    <path
      d="M 130 215 Q 175 175 220 215 Q 265 255 300 215"
      strokeDasharray="3 3"
      opacity="0.6"
    />
    {/* Discharge chute */}
    <path d="M 305 220 L 355 225 L 355 260 L 305 250 Z" opacity="0.85" />

    {/* Hopper loader (skip on left) */}
    <path d="M 60 170 L 110 195 L 110 240 L 60 220 Z" opacity="0.85" />
    <line
      x1="60"
      y1="220"
      x2="55"
      y2="245"
      strokeDasharray="2 3"
      opacity="0.55"
    />
    {/* Hopper rail up to drum */}
    <line
      x1="55"
      y1="245"
      x2="115"
      y2="200"
      strokeDasharray="3 2"
      opacity="0.55"
    />

    {/* Water tank (top-front) */}
    <rect x="115" y="95" width="90" height="45" rx="6" ry="6" />
    <line
      x1="115"
      y1="110"
      x2="205"
      y2="110"
      opacity="0.55"
      strokeDasharray="2 2"
    />
    <line x1="140" y1="140" x2="140" y2="155" opacity="0.6" />
    {/* Water metering electronics box */}
    <rect x="215" y="105" width="35" height="30" />
    <line x1="220" y1="115" x2="245" y2="115" opacity="0.6" />
    <line x1="220" y1="125" x2="245" y2="125" opacity="0.6" />

    {/* Joystick control station (right side) */}
    <rect x="260" y="105" width="40" height="45" />
    <line x1="280" y1="80" x2="280" y2="105" strokeWidth="2" />
    <circle cx="280" cy="78" r="4" />
    <line x1="280" y1="95" x2="288" y2="88" opacity="0.6" />

    {/* Dimension callout across drum length */}
    <line
      x1="105"
      y1="70"
      x2="305"
      y2="70"
      strokeDasharray="3 3"
      opacity="0.55"
    />
    <line x1="105" y1="65" x2="105" y2="75" opacity="0.6" />
    <line x1="305" y1="65" x2="305" y2="75" opacity="0.6" />
    <text
      x="180"
      y="60"
      fill="#FF6B1A"
      fontSize="10"
      fontFamily="Montserrat"
      opacity="0.7"
    >
      Ø 1050 L
    </text>

    {/* Component labels */}
    <text
      x="60"
      y="185"
      fill="#FF6B1A"
      fontSize="9"
      fontFamily="Montserrat"
      opacity="0.65"
    >
      HOPPER
    </text>
    <text
      x="125"
      y="132"
      fill="#FF6B1A"
      fontSize="9"
      fontFamily="Montserrat"
      opacity="0.65"
    >
      WATER TANK
    </text>
    <text
      x="265"
      y="98"
      fill="#FF6B1A"
      fontSize="9"
      fontFamily="Montserrat"
      opacity="0.65"
    >
      JOYSTICK
    </text>
    <text
      x="175"
      y="225"
      fill="#FF6B1A"
      fontSize="9"
      fontFamily="Montserrat"
      opacity="0.65"
    >
      REVERSIBLE DRUM
    </text>
    <text
      x="313"
      y="215"
      fill="#FF6B1A"
      fontSize="9"
      fontFamily="Montserrat"
      opacity="0.65"
    >
      CHUTE
    </text>
  </svg>
);

export default function ReversibleDrumMixerPage() {
  return (
    <>
      <Navbar />
      <ProductBreadcrumb productName="Reversible Drum Concrete Mixer (LRDM Series)" />

      {/* ─── SECTION 1: HERO ─── */}
      <ProductHero
        testId="lrdm-hero"
        backgroundImage={HERO_IMAGE}
        eyebrow="LRDM Series"
        title={
          <>
            LIFTILE Reversible
            <br />
            Drum Concrete{" "}
            <span className="text-liftile-orange">Mixer</span>
          </>
        }
        subtitle="Engineered for high-volume concrete production with superior mixing efficiency, positive hydraulic drum control and dependable site performance. Designed for commercial, industrial and infrastructure projects that demand continuous concrete supply."
        models={Object.keys(MODELS)}
        primaryCta={{ label: "Request a Quote", href: "/contact" }}
        secondaryCta={{ label: "Download Brochure" }}
        specs={HERO_SPECS}
        badges={HERO_BADGES}
      />

      {/* ─── SECTION 2: GALLERY ─── */}
      <ProductGallery
        testId="lrdm-gallery"
        heading={
          <>
            LRDM Series{" "}
            <span className="text-liftile-orange">In Action</span>
          </>
        }
        subtitle="A closer look at the LIFTILE LRDM Series Reversible Drum Concrete Mixer — from precision fabrication at our factory to real infrastructure projects across India."
        items={GALLERY}
        seriesLabel="LRDM Series"
        imageAlt="LIFTILE LRDM Series Reversible Drum Concrete Mixer"
      />

      {/* ─── SECTION 3: QUICK SPECS (split layout with 3 model tabs) ─── */}
      <QuickSpecsTabs
        testId="lrdm-quick-specs"
        heading={
          <>
            Quick <span className="text-liftile-orange">Specifications</span>
          </>
        }
        subtitle="The essentials — with a model selector to switch between LRDM600, LRDM800 and LRDM1050 configurations."
        quickSpecs={QUICK_SPECS}
        models={MODELS}
        defaultModel="LRDM800"
        footerNote={null}
      />

      {/* ─── SECTION 4: KEY FEATURES ─── */}
      <KeyFeatures
        testId="lrdm-features"
        heading={
          <>
            Built for High-Volume Output.{" "}
            <br className="hidden sm:block" />
            <span className="text-liftile-orange">
              Engineered for Hydraulic Precision.
            </span>
          </>
        }
        subtitle="Every LIFTILE LRDM Series Reversible Drum Concrete Mixer is engineered with premium branded components and rigorous quality inspections to deliver dependable batch after batch on demanding infrastructure projects."
        items={FEATURES}
      />

      {/* ─── SECTION 5: OPTIONAL ACCESSORIES ─── */}
      <OptionalAccessories
        testId="lrdm-accessories"
        heading={
          <>
            Optional <span className="text-liftile-orange">Accessories</span>
          </>
        }
        subtitle="Customize the LRDM Series for your specific project scale, batching standards and QC requirements with our range of purpose-built accessories."
        items={ACCESSORIES}
        gridCols="lg:grid-cols-3"
      />

      {/* ─── SECTION 6: APPLICATIONS ─── */}
      <Applications
        testId="lrdm-applications"
        heading={
          <>
            Ideal for Every{" "}
            <span className="text-liftile-orange">
              Infrastructure Project
            </span>
          </>
        }
        subtitle="From commercial developments to metros, bridges, expressways and industrial plants — the LRDM Series delivers consistent high-output concrete across every large-scale construction environment."
        items={APPLICATIONS}
        variant="dark"
        gridCols="lg:grid-cols-3"
      />

      {/* ─── SECTION 7: WHY CHOOSE LIFTILE ─── */}
      <WhyChooseLiftile
        testId="lrdm-why-choose"
        image="/product-reversible-drum-mixer.jpg"
        imageAlt="LIFTILE LRDM Series Reversible Drum Concrete Mixer — high-output hydraulic mixing"
        imageCaption="LRDM Series — high-output hydraulic mixing, heavy-duty build, dependable infrastructure-grade productivity."
        floatingBadge={{ top: "Up to", bottom: "17 m³/hr" }}
        eyebrow="Why LRDM Series"
        heading={
          <>
            Built for Continuous{" "}
            <br className="hidden sm:block" />
            <span className="text-liftile-orange">
              Infrastructure Output
            </span>
          </>
        }
        subtitle="The LRDM Series combines heavy-duty construction, positive hydraulic drum control and premium branded components to deliver dependable high-volume batch concrete on every infrastructure project."
        trustCards={TRUST_CARDS}
      />

      {/* ─── SECTION 8: TECHNICAL HIGHLIGHTS ─── */}
      <QualitySafetyCommitment
        testId="lrdm-tech-highlights"
        eyebrow="Technical Highlights"
        heading={
          <>
            Hydraulic Precision.{" "}
            <span className="text-liftile-orange">
              Infrastructure-Grade Build.
            </span>
          </>
        }
        subtitle="A closer look at the engineering foundations that make the LRDM Series a high-output, precision-controlled and long-lasting concrete mixer for modern infrastructure sites."
        items={TECH_HIGHLIGHTS}
      />

      {/* ─── SECTION 9: DOWNLOAD BROCHURE ─── */}
      <DownloadBrochure
        testId="lrdm-brochure"
        heading={
          <>
            Download Product{" "}
            <span className="text-liftile-orange">Brochure</span>
          </>
        }
        subtitle="Download the complete LRDM Series technical catalogue including specifications, drawings, features and product information for LRDM600, LRDM800 and LRDM1050 configurations."
        blueprintSvg={LRDM_BLUEPRINT_SVG}
      />

      {/* ─── SECTION 10: FAQ ─── */}
      <PremiumFAQ
        testId="lrdm-faq"
        heading={
          <>
            Frequently Asked{" "}
            <span className="text-liftile-orange">Questions</span>
          </>
        }
        subtitle="Everything builders, contractors and infrastructure engineers most often ask us about the LIFTILE LRDM Series Reversible Drum Concrete Mixer."
        items={FAQS}
      />

      {/* ─── SECTION 11: RELATED PRODUCTS ─── */}
      <RelatedProducts
        testId="lrdm-related"
        heading={
          <>
            Explore More{" "}
            <span className="text-liftile-orange">LIFTILE Solutions</span>
          </>
        }
        items={RELATED_PRODUCTS}
      />

      {/* ─── SECTION 12: FINAL CTA ─── */}
      <ProductFinalCTA
        testId="lrdm-product-cta"
        backgroundImage="/project-adani-korba.png"
        backgroundAlt="LIFTILE LRDM Series Reversible Drum Concrete Mixer on a live infrastructure site"
        heading={
          <>
            High Output.{" "}
            <span className="text-liftile-orange">Reliable Mixing.</span>
          </>
        }
        subtitle="The LIFTILE LRDM Series delivers efficient concrete production with advanced hydraulic operation, heavy-duty construction and dependable site performance."
        primaryCta={{ label: "Request a Quote", href: "/contact" }}
        secondaryCta={{ label: "Contact Sales Team", href: "/contact" }}
        trustBadges={CTA_BADGES}
      />

      <Footer />
    </>
  );
}
